import { TemplateTableData } from "./types";
import { ColumnDef } from "@tanstack/react-table";
import SortByIcon from "../../../../../assets/svg/sort-by-icon";
import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";
import {
  getChannelIcon,
  getModalityDisplayName,
} from "../../../utils/functions";
import VerbamailIcon from "../../../../../assets/svg/verbamail-icon";
import VerbamessengerIcon from "../../../../../assets/svg/verbamessenger-icon";
import VerbacallIcon from "../../../../../assets/svg/verbacall-icon";
import VerbasmsIcon from "../../../../../assets/svg/verbasms-icon";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { Tooltip } from "../../../../../components/Tooltip/Tooltip";
import { WORKFLO_NAME } from "../../../../../utils/constants/globalConstants";
import { Avatar } from "../../../../../components/Avatar/Avatar";
import { WatiTemplateCategory } from "../../../utils/types";
import { US_CLIENT_CODE } from "../../../../../utils/constants/mappings";
import DocumentTextIcon from "../../../../../assets/svg/document-text-icon";
import ClockIcon from "../../../../../assets/svg/clock-icon";
import CheckedIcon from "../../../../../assets/svg/checked-icon";
import CrossIcon from "../../../../../assets/svg/cross-icon";
import TrashIcon from "../../../../../assets/svg/trash-icon";
import PauseCircleIcon from "../../../../../assets/svg/pause-circle-icon";
import TemplateFavoriteButton from "../components/TemplateList/components/TemplateFavoriteButton";

export const TEMPLATE_TABLE_SORTABLE_FIELDS = [
  "title",
  "created_by",
  "created_at",
] as const;

export const createTemplatesDataColumns = (
  clientCode: string,
  onPreview?: (template: TemplateTableData) => void
): ColumnDef<TemplateTableData>[] => {
  const isUsClient = clientCode === US_CLIENT_CODE;

  return [
    {
      accessorKey: "title",
      size: 200,
      minSize: 150,
      header: () => (
        <div className="flex items-center gap-[4px]">
          <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Template Name
          </p>
          <SortByIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-[6px] overflow-hidden">
          <div
            role={onPreview ? "button" : undefined}
            onClick={
              onPreview
                ? (e) => {
                    e.stopPropagation();
                    onPreview(row.original);
                  }
                : undefined
            }
            className="min-w-0"
          >
            <TruncatedTextTooltip
              text={row.getValue("title") as string}
              maxWidth="max-w-full"
              className="text-md cursor-pointer font-medium hover:underline underline-offset-2 hover:text-verba-blue dark:hover:text-verba-blue-dk text-heading-text truncate dark:text-heading-text-dk"
            />
          </div>
          <TemplateFavoriteButton rowData={row.original} />
        </div>
      ),
    },
    {
      accessorKey: "use_case",
      size: 120,
      header: () => (
        <div className="flex items-center gap-[4px]">
          <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Use Case
          </p>
        </div>
      ),
      cell: ({ row }) => {
        const useCase = isUsClient
          ? (row.original.display_usecase_name ?? "")
          : (row.original.use_case?.replace(/_/g, " ") ?? "");
        const category = isUsClient
          ? (row.original.display_category_name ?? "")
          : (row.original.category?.replace(/_/g, " ") ?? "");
        return (
          <div className="flex flex-col overflow-hidden">
            <TruncatedTextTooltip
              text={category}
              maxWidth="max-w-full"
              className="text-md font-medium capitalize truncate text-heading-text dark:text-heading-text-dk"
            />
            <TruncatedTextTooltip
              text={useCase}
              maxWidth="max-w-full"
              className="text-sm font-normal capitalize text-dark-gray truncate dark:text-dark-gray-dk"
            />
          </div>
        );
      },
    },
    {
      accessorKey: "modality",
      size: 60,
      header: () => (
        <div>
          <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Mode
          </p>
        </div>
      ),
      cell: ({ row }) => (
        <div>
          <Tooltip content={getModalityDisplayName(row.getValue("modality"))}>
            <div className="flex flex-col items-center justify-center rounded-full w-[24px] h-[24px] border border-gray-50 dark:border-gray-50-dk">
              {getChannelIcon(row.getValue("modality"))}
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      accessorKey: "branch",
      size: 120,
      header: () => (
        <div className="flex items-center gap-[4px]">
          <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Branch
          </p>
        </div>
      ),
      cell: ({ row }) => {
        const branch = isUsClient
          ? (row.original.display_branch_name ?? "")
          : (row.original.branch?.replace(/_/g, " ") ?? "");
        return (
          <div className="flex flex-col overflow-hidden">
            <TruncatedTextTooltip
              text={branch}
              maxWidth="max-w-full"
              className="text-md font-medium capitalize truncate text-heading-text dark:text-heading-text-dk"
            />
          </div>
        );
      },
    },
    {
      accessorKey: "created_by",
      size: 140,
      header: () => (
        <div className="flex items-center gap-[4px]">
          <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Created By
          </p>
          <SortByIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
        </div>
      ),
      cell: ({ row }) => {
        const createdByName = (row.getValue("created_by") as { name: string })
          ?.name;
        const createdBy =
          createdByName && createdByName.trim() !== ""
            ? createdByName
            : "Verbaflo";
        return (
          <div className="flex flex-col gap-[2px] overflow-hidden">
            <div className="flex items-center gap-[4px]">
              <Avatar
                text={createdBy}
                size={18}
                className="p-[2px]! text-[8px]! shrink-0"
              />
              <TruncatedTextTooltip
                text={createdBy}
                maxWidth="max-w-full"
                className="text-md font-normal truncate text-heading-text dark:text-heading-text-dk"
              />
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      size: 130,
      header: () => (
        <div className="flex items-center gap-[4px]">
          <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Created On
          </p>
          <SortByIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
        </div>
      ),
      cell: ({ row }) => {
        const dateTime = getDateTimeByFormat(
          new Date(row.getValue("created_at"))
        );
        return (
          <div className="flex flex-col gap-[2px] overflow-hidden">
            <TruncatedTextTooltip
              text={dateTime.date}
              maxWidth="max-w-full"
              className="text-md font-normal truncate text-heading-text dark:text-heading-text-dk"
            />
            <TruncatedTextTooltip
              text={dateTime.time}
              maxWidth="max-w-full"
              className="text-sm font-normal truncate text-dark-gray dark:text-dark-gray-dk"
            />
          </div>
        );
      },
    },
  ];
};

export const MODALITY_TEXT_MAP = {
  whatsapp: "VerbaMessenger",
  email: "VerbaMail",
  call: "VerbaCall",
  sms: "VerbaSMS",
};

export const TEMPLATE_FORM_STEPS = [
  {
    key: "template-library",
    id: 1,
    title: "Template Library",
  },
  {
    key: "template-details",
    id: 2,
    title: "Details",
  },
  {
    key: "template-builder",
    id: 3,
    title: "Builder",
  },
];

export enum TEMPLATE_MODALITY_ENUM {
  EMAIL = "email",
  CALL = "call",
  WHATSAPP = "whatsapp",
  SMS = "sms",
}

export enum WHATSAPP_TEMPLATE_HEADER_TYPE {
  NONE = "none",
  TEXT = "text",
  IMAGE = "image",
  DOCUMENT = "document",
}

export const WATI_STATUS_DISPLAY: Record<
  number,
  {
    label: string;
    Icon: React.ElementType;
    iconClass: string;
    textColor: string;
  }
> = {
  0: {
    label: "Draft",
    Icon: DocumentTextIcon,
    iconClass:
      "w-3 h-3 shrink-0 text-gray-placeholder dark:text-gray-placeholder-dk",
    textColor: "text-gray-placeholder dark:text-gray-placeholder-dk",
  },
  1: {
    label: "Pending",
    Icon: ClockIcon,
    iconClass: "w-3 h-3 shrink-0 text-tag-scheduled dark:text-tag-scheduled-dk",
    textColor: "text-tag-scheduled dark:text-tag-scheduled-dk",
  },
  2: {
    label: "Approved",
    Icon: CheckedIcon,
    iconClass:
      "w-3 h-3 shrink-0 text-verba-green-success dark:text-verba-green-success-dk",
    textColor: "text-verba-green-success dark:text-verba-green-success-dk",
  },
  3: {
    label: "Rejected",
    Icon: CrossIcon,
    iconClass:
      "w-4 h-4 shrink-0 text-verba-red-alert dark:text-verba-red-alert-dk",
    textColor: "text-verba-red-alert dark:text-verba-red-alert-dk",
  },
  4: {
    label: "Deleted",
    Icon: TrashIcon,
    iconClass:
      "w-3 h-3 shrink-0 text-verba-red-alert dark:text-verba-red-alert-dk",
    textColor: "text-verba-red-alert dark:text-verba-red-alert-dk",
  },
  5: {
    label: "Pending",
    Icon: ClockIcon,
    iconClass: "w-3 h-3 shrink-0 text-tag-scheduled dark:text-tag-scheduled-dk",
    textColor: "text-tag-scheduled dark:text-tag-scheduled-dk",
  },
  6: {
    label: "Disabled",
    Icon: CrossIcon,
    iconClass:
      "w-4 h-4 shrink-0 text-verba-red-alert dark:text-verba-red-alert-dk",
    textColor: "text-verba-red-alert dark:text-verba-red-alert-dk",
  },
  7: {
    label: "Paused",
    Icon: PauseCircleIcon,
    iconClass:
      "w-4 h-4 shrink-0 text-dark-yellow-for-alerts dark:text-dark-yellow-for-alerts-dk",
    textColor:
      "text-dark-yellow-for-alerts dark:text-dark-yellow-for-alerts-dk",
  },
};

export const WATI_QUALITY_DISPLAY: Record<
  number,
  { label: string; textColor: string }
> = {
  0: {
    label: "-",
    textColor: "text-gray-placeholder dark:text-gray-placeholder-dk",
  },
  1: {
    label: "High",
    textColor: "text-verba-green-success dark:text-verba-green-success-dk",
  },
  2: {
    label: "Low",
    textColor: "text-verba-red-alert dark:text-verba-red-alert-dk",
  },
  3: {
    label: "Medium",
    textColor:
      "text-dark-yellow-for-alerts dark:text-dark-yellow-for-alerts-dk",
  },
};

export const TEMPLATE_DETAILS_DEFAULT_VALUES = {
  email: {
    templateName: "",
    modality: TEMPLATE_MODALITY_ENUM.EMAIL,
    category: "",
    useCase: "",
    branch: "",
    subject: "",
  },
  call: {
    templateName: "",
    modality: TEMPLATE_MODALITY_ENUM.CALL,
    category: "",
    useCase: "",
    branch: "",
  },
  whatsapp: {
    modality: TEMPLATE_MODALITY_ENUM.WHATSAPP,
    category: "",
    useCase: "",
    branch: "",
  },
  sms: {
    templateName: "",
    modality: TEMPLATE_MODALITY_ENUM.SMS,
    category: "",
    useCase: "",
    branch: "",
  },
};

export const TEMPLATE_MODALITY_DEFAULT_VALUES = {
  email: {
    emailContent: "",
    emailBuilderDesign: null,
  },
  call: {
    assistantName: "",
    initialMessage: "",
    endCallMessage: "",
    voiceUrl: "",
    voiceId: "",
    callContext: "",
    callScript: "",
    voice_speed: "",
    voice_provider: "",
  },
  whatsapp: {
    templateName: "",
    broadcastName: "",
    body: "",
    language: "",
    buttons: [],
    footer: null,
    header: null,
    watiCategory: null,
  },
  sms: {
    smsContent: "",
  },
};

export const TEMPLATE_MODALITY_FILTER_OPTIONS = [
  {
    value: "email",
    content: (
      <div className="flex items-center gap-[10px]">
        <VerbamailIcon className="w-[18px] h-[18px] text-verba-mail dark:text-verba-mail-dk" />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            VerbaMail
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            E-Mail {WORKFLO_NAME.singular} Templates
          </p>
        </div>
      </div>
    ),
    selectedOptionContent: (
      <div className="flex items-center gap-[4px]">
        <VerbamailIcon className="w-[14px] h-[14px] text-verba-mail dark:text-verba-mail-dk" />
        <h3 className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
          VerbaMail
        </h3>
      </div>
    ),
  },
  {
    value: "whatsapp",
    content: (
      <div className="flex items-center gap-[10px]">
        <VerbamessengerIcon className="w-[18px] h-[18px] text-verba-messenger dark:text-verba-messenger-dk" />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            VerbaMessenger
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            WhatsApp {WORKFLO_NAME.singular} Templates
          </p>
        </div>
      </div>
    ),
    selectedOptionContent: (
      <div className="flex items-center gap-[4px]">
        <VerbamessengerIcon className="w-[14px] h-[14px] text-verba-messenger dark:text-verba-messenger-dk" />
        <h3 className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
          VerbaMessenger
        </h3>
      </div>
    ),
  },
  {
    value: "call",
    content: (
      <div className="flex items-center gap-[10px]">
        <VerbacallIcon className="w-[18px] h-[18px] text-verba-lavender dark:text-verba-lavender-dk" />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            VerbaCall
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            Call {WORKFLO_NAME.singular} Templates
          </p>
        </div>
      </div>
    ),
    selectedOptionContent: (
      <div className="flex items-center gap-[4px]">
        <VerbacallIcon className="w-[14px] h-[14px] text-verba-lavender dark:text-verba-lavender-dk" />
        <h3 className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
          VerbaCall
        </h3>
      </div>
    ),
  },
  // {
  //   value: "sms",
  //   content: (
  //     <div className="flex items-center gap-[10px]">
  //       <VerbasmsIcon className="w-[18px] h-[18px] text-verba-sms dark:text-verba-sms-dk" />
  //       <div>
  //         <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
  //           VerbaSMS
  //         </h3>
  //         <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
  //           SMS {WORKFLO_NAME.singular} Templates
  //         </p>
  //       </div>
  //     </div>
  //   ),
  //   selectedOptionContent: (
  //     <div className="flex items-center gap-[4px]">
  //       <VerbasmsIcon className="w-[14px] h-[14px] text-verba-sms dark:text-verba-sms-dk" />
  //       <h3 className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
  //         VerbaSMS
  //       </h3>
  //     </div>
  //   ),
  // },
];

export const TEMPLATE_CATEGORY_FILTER_OPTIONS = [
  {
    value: "sales",
    content: (
      <div className="flex items-center gap-[10px]">
        <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
          Sales
        </h3>
      </div>
    ),
  },
  {
    value: "marketing",
    content: (
      <div className="flex items-center gap-[10px]">
        <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
          Marketing
        </h3>
      </div>
    ),
  },
  {
    value: "finance",
    content: (
      <div className="flex items-center gap-[10px]">
        <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
          Finance
        </h3>
      </div>
    ),
  },
  {
    value: "operations",
    content: (
      <div className="flex items-center gap-[10px]">
        <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
          Operations
        </h3>
      </div>
    ),
  },
  {
    value: "maintenance",
    content: (
      <div className="flex items-center gap-[10px]">
        <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
          Maintenance
        </h3>
      </div>
    ),
  },
];

export const templateHeaderMapping = {
  email: ["created_by", "use_case", "modality", "subject"],
  call: ["created_by", "use_case", "modality", "tone", "assistant_name"],
  whatsapp: ["created_by", "use_case", "modality"],
  sms: ["created_by", "use_case", "modality"],
};

export const whatsappTemplateCategoryOptions = [
  {
    label: "Utility",
    value: WatiTemplateCategory.UTILITY,
    content: "Utility",
  },
  {
    label: "Marketing",
    value: WatiTemplateCategory.MARKETING,
    content: "Marketing",
  },
  {
    label: "Authentication",
    value: WatiTemplateCategory.AUTHENTICATION,
    content: "Authentication",
  },
];

export const whatsappTemplateTypeOptions = [
  {
    label: "Text",
    value: "text",
    content: "Text",
  },
  {
    label: "Image",
    value: "image",
    content: "Image",
  },
  {
    label: "Video",
    value: "video",
    content: "Video",
  },
  {
    label: "Document",
    value: "document",
    content: "Document",
  },
  {
    label: "Location",
    value: "location",
    content: "Location",
  },
  {
    label: "Carousel",
    value: "carousel",
    content: "Carousel",
  },
  {
    label: "limited-time-offer",
    value: "limited-time-offer",
    content: "Limited Time Offer",
  },
];
